import { FormatDateOptions, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { LabelM } from '@sorare/core/src/atoms/typography';
import { LiveIndicator } from '@sorare/core/src/atoms/ui/LiveIndicator';
import { Tag } from '@sorare/core/src/atoms/ui/Tag';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { glossary } from '@sorare/core/src/lib/glossary';

const State = styled(Tag)`
  display: inline-flex;
  font-size: 12px;
  line-height: 20px;
  padding: 0px 0.5em;
  gap: var(--half-unit);
  white-space: nowrap;
`;

const PastGameHeader = styled.div`
  display: flex;
  gap: var(--unit);
`;

type Props = {
  startDate: Date;
  status: string;
  quarter: number;
  isHalftime: boolean;
  className?: string;
  hideDay?: boolean;
  forceDateFormatDisplay?: FormatDateOptions;
};

export const GameTimeStatusTag = ({
  startDate,
  status,
  quarter,
  isHalftime,
  className,
  hideDay = false,
  forceDateFormatDisplay,
}: Props) => {
  const { formatDate, formatMessage } = useIntlContext();
  const fullDate = formatDate(startDate, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const dateNoTime = formatDate(startDate, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  const dateFormat: FormatDateOptions = hideDay
    ? {
        hour: '2-digit',
        minute: '2-digit',
      }
    : {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      };

  const date = formatDate(startDate, forceDateFormatDisplay || dateFormat);

  if (status === 'playing') {
    return (
      <State color="grey" className={className} title={fullDate}>
        <LiveIndicator />{' '}
        {isHalftime ? (
          <FormattedMessage id="Game.status.halftime" defaultMessage="Half" />
        ) : (
          quarter !== null && (
            <span>{formatMessage(glossary.ordinal, { ordinal: quarter })}</span>
          )
        )}
      </State>
    );
  }

  if (status === 'played' && !forceDateFormatDisplay) {
    return (
      <PastGameHeader>
        <State color="black" className={className}>
          <FormattedMessage
            id="Game.status.final"
            defaultMessage="Final"
            values={{
              date,
              dot: (
                <svg width={5} height={5} fill="currentColor">
                  <circle r={2} cx={2} cy={2} />
                </svg>
              ),
            }}
          />
        </State>
        <LabelM>{dateNoTime}</LabelM>
      </PastGameHeader>
    );
  }

  if (status === 'played' && forceDateFormatDisplay) {
    return (
      <State color="black" className={className}>
        {date}
      </State>
    );
  }

  if (status === 'suspended') {
    return (
      <State color="red" $stroke className={className} title={fullDate}>
        <FormattedMessage
          id="Game.status.suspended"
          defaultMessage="Suspended"
          values={{
            date,
            dot: (
              <svg width={5} height={5} fill="currentColor">
                <circle r={2} cx={2} cy={2} />
              </svg>
            ),
          }}
        />
      </State>
    );
  }

  if (status === 'canceled') {
    return (
      <State color="red" $stroke className={className} title={fullDate}>
        <FormattedMessage
          id="Game.status.canceled"
          defaultMessage="Canceled"
          values={{
            date,
            dot: (
              <svg width={5} height={5} fill="currentColor">
                <circle r={2} cx={2} cy={2} />
              </svg>
            ),
          }}
        />
      </State>
    );
  }

  return (
    <State color="grey" className={className} title={fullDate}>
      {date}
    </State>
  );
};
